import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, User as UserIcon, ArrowRight } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { formatApiError } from "@/lib/api";
import BrandLogo from "@/components/BrandLogo";
import OAuthButtons from "@/components/OAuthButtons";

export default function Register() {
  const { register, user } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => {
    if (user && !user.is_guest) navigate("/app/dashboard", { replace: true });
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await register(email.trim(), password, name.trim());
      navigate("/app/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex relative overflow-hidden">
      <div className="ambient-bg" />
      <div className="hidden md:flex flex-1 relative border-r border-zinc-900 p-12 flex-col justify-between">
        <BrandLogo variant="lockup" iconSize={36} to="/" testid="register-brand" />
        <div>
          <div className="text-[11px] uppercase tracking-[0.24em] text-amber-400 mb-4">
            get started
          </div>
          <h2 className="text-4xl font-bold tracking-tight text-zinc-50 leading-tight">
            Prepare like it&apos;s
            <br />
            the <span className="text-amber-400">real interview</span>.
          </h2>
          <p className="mt-6 text-zinc-400 max-w-md leading-relaxed">
            Align your resume to the job, generate a tailored cover letter, practice personalized
            AI interviews, and measure your Interview Readiness, all in one guided experience.
          </p>
        </div>
        <div className="text-xs text-zinc-600 tracking-widest uppercase">© 2026 Intuiterview</div>
      </div>
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md animate-in">
          <div className="mb-8">
            <h1 className="text-2xl font-semibold tracking-tight text-zinc-50">Create your account</h1>
            <p className="text-sm text-zinc-500 mt-1">It takes 20 seconds.</p>
          </div>

          <OAuthButtons testidPrefix="register" />

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full h-px bg-zinc-800" /></div>
            <div className="relative flex justify-center text-[10px] uppercase tracking-widest">
              <span className="bg-[#09090B] px-3 text-zinc-600">or with email</span>
            </div>
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">
                Full name
              </label>
              <div className="relative">
                <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-600" />
                <input
                  data-testid="register-name-input"
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-zinc-900/60 border border-zinc-800 rounded-lg pl-10 pr-3 py-2.5 text-sm text-zinc-50 placeholder:text-zinc-600 focus:border-zinc-700 focus:outline-none transition-colors"
                  placeholder="Jordan Rivera"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-600" />
                <input
                  data-testid="register-email-input"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-zinc-900/60 border border-zinc-800 rounded-lg pl-10 pr-3 py-2.5 text-sm text-zinc-50 placeholder:text-zinc-600 focus:border-zinc-700 focus:outline-none transition-colors"
                  placeholder="you@company.com"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-600" />
                <input
                  data-testid="register-password-input"
                  type="password"
                  required
                  minLength={6}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-zinc-900/60 border border-zinc-800 rounded-lg pl-10 pr-3 py-2.5 text-sm text-zinc-50 placeholder:text-zinc-600 focus:border-zinc-700 focus:outline-none transition-colors"
                  placeholder="At least 6 characters"
                />
              </div>
            </div>

            {error && (
              <div
                data-testid="register-error"
                className="text-xs text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              data-testid="register-submit-btn"
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 bg-amber-500 text-amber-950 hover:bg-amber-400 disabled:opacity-60 font-semibold rounded-lg px-4 py-2.5 transition-colors"
            >
              {submitting ? "Creating account…" : "Create account"}
              {!submitting && <ArrowRight className="h-4 w-4" />}
            </button>
          </form>

          <div className="mt-6 text-sm text-zinc-500">
            Already have an account?{" "}
            <Link
              to="/login"
              data-testid="register-to-login-link"
              className="text-zinc-200 hover:text-white font-medium"
            >
              Sign in
            </Link>
          </div>

          <div className="mt-8 pt-6 border-t border-zinc-900 text-[11px] text-zinc-600 leading-relaxed">
            By creating an account you agree to our{" "}
            <Link to="/trust/terms" data-testid="register-terms-link" className="text-zinc-400 hover:text-zinc-200 underline underline-offset-4">
              Terms of Service
            </Link>{" "}
            and acknowledge our{" "}
            <Link to="/trust/privacy" data-testid="register-privacy-link" className="text-zinc-400 hover:text-zinc-200 underline underline-offset-4">
              Privacy Policy
            </Link>. Visit our{" "}
            <Link to="/trust" data-testid="register-trust-link" className="text-amber-400 hover:text-amber-300 underline underline-offset-4">
              Trust &amp; Privacy center
            </Link>{" "}
            for more.
          </div>
        </div>
      </div>
    </div>
  );
}
